/**
 * useFrequentPages
 *
 * Resolves the user's most-visited pages into a list suitable for the
 * "Frequent" quick-nav in the TopNav.
 *
 * Responsibilities:
 * 1. Track every route change (via {@link useFrequentPagesTracker}) and
 *    record visits against the canonical route pattern, looked up from
 *    the Page Registry first, then the Navigation Registry, falling
 *    back to a generic normalization for unregistered paths.
 * 2. Bind the frequent-pages store to the authenticated user so history
 *    is scoped per-user and cleared on logout.
 * 3. Expose the top-10 visited pages enriched with label + icon, joined
 *    from the Page and Navigation registries so the dropdown can render
 *    friendly names without re-querying the registries on every render.
 */

import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import {
  useFrequentPagesStore,
  normalizePath,
  pickTopFrequent,
  FREQUENT_PAGES_LIMIT,
  type FrequentPageEntry,
} from '../stores/frequentPagesStore';
import { usePageRegistry } from '../plugins/registries/page-registry';
import type { PluginPageDefinition } from '../plugins/registries/page-registry';
import { useNavigationRegistry } from '../plugins/registries/navigation-registry';
import type { NavigationItem, NavigationCategory } from '../plugins/registries/navigation-registry';

export interface FrequentPageLink {
  /** Canonical route pattern (used as the React Router `to` target). */
  path: string;
  /** Display label (best-effort, falls back to the path). */
  label: string;
  /** Icon name (lucide / design-system icon string), if known. */
  icon?: string;
  /** Visit count (useful for ranking displays). */
  count: number;
  /** ISO timestamp of the most recent visit. */
  lastVisitedAt: string;
}

/**
 * Resolve a raw pathname to its canonical route pattern.
 *
 * Lookup order:
 *   1. Page Registry (plugin + core pages) — exact, wildcard, and
 *      `:param` patterns. This yields patterns like "/workflows/:id/edit".
 *   2. Navigation Registry items + category items — patterns are matched
 *      by exact path or by replacing dynamic segments heuristically.
 *   3. Generic normalization via {@link normalizePath}.
 */
export function resolveRoutePattern(rawPath: string): string {
  if (!rawPath) return '/';

  // 1. Page registry — supports exact, wildcard, and `:param` patterns.
  const pageStore = usePageRegistry.getState();
  const page = pageStore.getPageByPath(rawPath);
  if (page?.path) return page.path;

  // 2. Navigation registry — flatten items + category items + children.
  const navState = useNavigationRegistry.getState();
  const navMatch = findNavMatch([...navState.items, ...navState.categories], rawPath);
  if (navMatch) return navMatch;

  // 3. Generic fallback normalization.
  return normalizePath(rawPath);
}

function findNavMatch(sources: Array<NavigationItem | NavigationCategory>, rawPath: string): string | null {
  for (const source of sources) {
    if ('path' in source && source.path) {
      if (pathsMatch(source.path, rawPath)) return source.path;
    }
    if ('items' in source && Array.isArray(source.items)) {
      const nested = findNavMatch(source.items, rawPath);
      if (nested) return nested;
    }
    if ('children' in source && Array.isArray((source as NavigationItem).children)) {
      const nested = findNavMatch((source as NavigationItem).children as NavigationItem[], rawPath);
      if (nested) return nested;
    }
  }
  return null;
}

function pathsMatch(pattern: string, rawPath: string): boolean {
  if (pattern === rawPath) return true;
  if (!pattern.includes(':')) return false;

  const patternParts = pattern.split('/').filter(Boolean);
  const pathParts = rawPath.split('/').filter(Boolean);
  if (patternParts.length !== pathParts.length) return false;

  for (let i = 0; i < patternParts.length; i++) {
    if (patternParts[i].startsWith(':')) continue;
    if (patternParts[i] !== decodeURIComponent(pathParts[i])) return false;
  }
  return true;
}

/**
 * Resolve a canonical route pattern to a friendly label + icon.
 *
 * Lookup order mirrors {@link resolveRoutePattern}: page registry first,
 * then navigation registry, then a derived label from the path segments.
 */
function resolveLinkMetadata(
  pattern: string,
  pages: Map<string, PluginPageDefinition>,
  navItems: NavigationItem[],
  navCategories: NavigationCategory[],
): { label: string; icon?: string } {
  // 1. Page registry exact match on path.
  for (const page of pages.values()) {
    if (page.path === pattern) {
      return { label: page.title, icon: page.icon };
    }
  }

  // 2. Navigation registry lookup.
  const navItem = findNavItemByPattern(navItems, pattern) ?? findNavItemByPattern(navCategories, pattern);
  if (navItem) {
    return { label: navItem.label, icon: navItem.icon };
  }

  // 3. Derive a label from the path's last static segment.
  const segments = pattern.split('/').filter((segment) => segment && !segment.startsWith(':'));
  if (segments.length === 0) return { label: 'Home' };

  const last = segments[segments.length - 1];
  const label = last
    .split(/[-_]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  return { label };
}

function findNavItemByPattern(
  sources: Array<NavigationItem | NavigationCategory>,
  pattern: string,
): NavigationItem | null {
  for (const source of sources) {
    if ('path' in source && source.path === pattern) return source as NavigationItem;
    if ('items' in source && Array.isArray(source.items)) {
      const nested = findNavItemByPattern(source.items, pattern);
      if (nested) return nested;
    }
    if ('children' in source && Array.isArray((source as NavigationItem).children)) {
      const nested = findNavItemByPattern((source as NavigationItem).children as NavigationItem[], pattern);
      if (nested) return nested;
    }
  }
  return null;
}

/**
 * Track route changes and record visits against the resolved pattern.
 * Mount this hook once near the top of the app (e.g. inside TopNav).
 */
export function useFrequentPagesTracker(): void {
  const location = useLocation();
  const recordVisit = useFrequentPagesStore((state) => state.recordVisit);
  const userId = useAuthStore((state) => state.userId);
  const setUserId = useFrequentPagesStore((state) => state.setUserId);

  // Keep the frequent-pages store bound to the authenticated user so
  // history is partitioned per-user and reset on login/logout/switch.
  useEffect(() => {
    setUserId(userId);
  }, [userId, setUserId]);

  // Record a visit whenever the pathname changes.
  useEffect(() => {
    // Skip auth / login routes — they shouldn't surface in the Frequent list.
    if (!userId) return;
    if (location.pathname === '/login') return;

    const pattern = resolveRoutePattern(location.pathname);
    recordVisit(pattern);
  }, [location.pathname, recordVisit, userId]);
}

/**
 * Return the current user's top-10 most-visited pages, enriched with
 * label + icon. Re-derives whenever the underlying store, page registry,
 * or navigation registry changes.
 */
export function useFrequentPages(limit: number = FREQUENT_PAGES_LIMIT): FrequentPageLink[] {
  const entries = useFrequentPagesStore((state) => state.entries);
  const pages = usePageRegistry((state) => state.pages);
  const navItems = useNavigationRegistry((state) => state.items);
  const navCategories = useNavigationRegistry((state) => state.categories);

  return useMemo(() => {
    const top = pickTopFrequent(entries, limit);
    if (top.length === 0) return [];

    return top.map((entry: FrequentPageEntry) => {
      const meta = resolveLinkMetadata(entry.path, pages, navItems, navCategories);
      return {
        path: entry.path,
        label: meta.label,
        icon: meta.icon,
        count: entry.count,
        lastVisitedAt: entry.lastVisitedAt,
      };
    });
  }, [entries, pages, navItems, navCategories, limit]);
}

export { FREQUENT_PAGES_LIMIT };